import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SVGIcon from '../../assets/images/svg/SVGIcon';
import AppLayout from '../../component/appLayout';
import {
  Group,
  HeaderBlock,
  Heading,
  Icon,
  Title,
  TitleWrap,
  UserDetails,
  Username,
  UserDescription,
  UserMail,
  ProfileIcon,
  Userprofile,
  Profile,
  OptionMenu,
  HeaderLeft,
  PaginationSection,
  LeftDiv,
  RightDiv,
  Text,
  ArrowBtn,
  ShowRow,
  RowText,
  Downarrow,
  TopBlock,
  TableWrapper,
  Table,
  TableHead,
  TableHeader,
  TableRow,
  TableData
} from './styles';
import { getCustomerList, updateCustomerData } from '../../services/customerServices';
import CustomerModal from '../../component/modals/customerModal';
import { CustomerDetailsInterface } from '../../interfaces/CustomerInterface';
import { isEmpty } from '../../helpers/common';
import Button from '../../component/button';
import DeleteModal from '../../component/modals/deleteModal';
import Modal from '../../component/modals/modal';
import LoadingState from '../../component/loading/loadingState';
import { useHistory } from 'react-router-dom';
import { captureException } from '../../services/logService';
import {
  BUTTON_TYPE,
  CustomerDetailMoreMenu,
  EMPTY_STATE_SIZE,
  ICON_BUTTON_SIZE,
  ICON_BUTTON_TYPE,
  ICON_SIZE,
  MENU_OPTIONS
} from '../../global/constants';
import UserPreferenceSingleton from '../../helpers/userPreferenceSingleton';
import { Dropdown, DropdownItem } from '../../component/dropdown';
import { rootStore } from '../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import { useMobileDevice } from '../../global/useMobile';
import ResponsiveHeader from '../../component/responsiveHeader';
import BottomSubNavigation from '../../component/bottomNavigation/subNavigation';
import BottomNavigationItem from '../../component/bottomNavigation/subNavigation/bottomNavigationItem';
import IconButton from '../../component/button/iconButton';
import EmptyStateV2 from '../../component/emptyState';
import Image from '../../component/image';
import { ROUTES } from '../../routes/urls';

const Customer: React.FC = () => {
  //Refs
  const buttonRef = useRef<React.RefObject<HTMLDivElement>[]>([]);

  //States
  const [customerListIndex, setCustomerListIndex] = useState({ start: 1, end: 10 });
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [open, setOpen] = React.useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageData, setPageData] = useState({
    currentPage: 1,
    numOfData: 100
  });
  const [customerLoading, setCustomerLoading] = useState(false);
  const [filteredCustomerList, setFilteredCustomerList] = useState<CustomerDetailsInterface[]>([]);
  const [activeDropdownId, setActiveDropdownId] = useState<string | null>(null);

  // mobx store variables
  const { workspaceStore, customerStore } = rootStore;
  const { currentWorkspace: workspace } = workspaceStore;
  const { customers, newCustomer } = customerStore;
  const workspace_id = workspace?.id;

  //Other variable
  const handleOpen = () => setOpen(true);
  const history = useHistory();
  const existingPageData = UserPreferenceSingleton.getInstance().getCustomerPagination();
  const rowOptions = [10, 20, 50, 100];
  const isMobile = useMobileDevice();

  // for close model
  const handleClose = () => {
    customerStore.clearCustomerDetail();
    setOpen(false);
  };

  // manage pagination
  const handleIndexChange = useCallback(
    (customerList: CustomerDetailsInterface[], currentPage: number, numOfData: number) => {
      const startIndex = Math.min((currentPage - 1) * numOfData + 1, customerList?.length ?? 0);
      const endIndex = Math.min(currentPage * numOfData, customerList?.length ?? 0);
      setCustomerListIndex({ start: startIndex, end: endIndex });
    },
    []
  );

  // load initial data
  const loadData = useCallback(async () => {
    try {
      if (!isEmpty(workspace_id)) {
        setCustomerLoading(true);
        const storedRowsPerPage = existingPageData?.[workspace_id || ''];
        if (storedRowsPerPage && pageData.numOfData !== Number(storedRowsPerPage)) {
          setPageData({
            ...pageData,
            numOfData: Number(storedRowsPerPage)
          });
        }
        const customerList = await getCustomerList();
        let subData;
        if (pageData?.currentPage && pageData?.numOfData) {
          const currentIndex = pageData?.numOfData * (pageData?.currentPage - 1);
          subData = customerList?.slice(currentIndex, currentIndex + pageData?.numOfData);
        } else {
          subData = customerList?.slice(0, 100);
        }
        handleIndexChange(customerList, pageData?.currentPage, pageData?.numOfData);
        setFilteredCustomerList(subData || []);
      }
    } catch (error) {
      captureException(error);
    } finally {
      setCustomerLoading(false);
    }
  }, [workspace_id, existingPageData, pageData, handleIndexChange]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    return () => {
      customerStore.setCustomers([]);
    };
  }, [customerStore]);

  // for edit customer details
  const onClickEdit = useCallback(
    (item: CustomerDetailsInterface) => {
      customerStore.setCustomerDetail(item);
      setOpen(true);
    },
    [customerStore]
  );

  // load pagination data
  const loadPaginationData = useCallback(
    (currentPage: number, numOfData: number) => {
      const startingIndex = numOfData * (currentPage - 1);
      const endingIndex = startingIndex + numOfData > customers?.length ? customers?.length : startingIndex + numOfData;
      const subData = customers?.slice(startingIndex, endingIndex);
      setFilteredCustomerList(subData || []);
    },
    [customers]
  );

  // handel menu click
  const handleMenuClick = useCallback(
    ({ key }: { key: string }) => {
      const rowsPerPage = parseInt(key);
      const pageData = {
        ...(existingPageData || {}),
        [workspace_id || '']: rowsPerPage
      };
      UserPreferenceSingleton.getInstance().setCustomerPagination(pageData);
      setPageData((prevPageData) => {
        // Recalculate current page based on the new number of rows per page
        const newPage = Math.floor(((prevPageData.currentPage - 1) * prevPageData.numOfData) / rowsPerPage) + 1;
        const updatedPageData = { ...prevPageData, currentPage: newPage, numOfData: rowsPerPage };
        handleIndexChange(customers, newPage, rowsPerPage);
        loadPaginationData(newPage, rowsPerPage);
        return updatedPageData;
      });
    },
    [customers, existingPageData, handleIndexChange, loadPaginationData, workspace_id]
  );

  // for change page
  const onChangePage = useCallback(
    (direction: 'Forward' | 'Backward') => {
      setPageData((prevPageData) => {
        let updatedPage = prevPageData.currentPage;

        if (direction === 'Forward' && prevPageData.currentPage * prevPageData.numOfData < (customers?.length ?? 0)) {
          updatedPage += 1;
        } else if (direction === 'Backward' && prevPageData.currentPage > 1) {
          updatedPage -= 1;
        }

        handleIndexChange(customers, updatedPage, prevPageData.numOfData);
        loadPaginationData(updatedPage, prevPageData.numOfData);

        return { ...prevPageData, currentPage: updatedPage };
      });
    },
    [customers, handleIndexChange, loadPaginationData]
  );

  // for delete customer
  const onClickDelete = useCallback(
    (item: CustomerDetailsInterface) => {
      customerStore.setCustomerDetail(item);
      setIsDeleteOpen(true);
    },
    [customerStore]
  );

  // for close modal
  const onCloseModal = useCallback(() => {
    customerStore.clearCustomerDetail();
    setIsDeleteOpen(false);
  }, [customerStore]);

  // conform delete customer
  const onDeleteCustomer = useCallback(
    async (item: { id: string }) => {
      try {
        setLoading(true);
        let response;
        if (!isEmpty(item?.id)) {
          response = await updateCustomerData(item?.id, { IsArchived: true });
        }
        if (response) {
          onCloseModal();
          rootStore.alertNotificationStore.setSuccessMessage('Customer deleted successfully!');
          loadData();
        }
      } catch (e) {
        captureException(e);
      } finally {
        setLoading(false);
      }
    },
    [loadData, onCloseModal]
  );

  // handle click
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleClick = (key: number | string, item: CustomerDetailsInterface) => {
    key = Number(key);
    if (key === MENU_OPTIONS.EDIT) {
      onClickEdit(item);
    } else if (key === MENU_OPTIONS.DELETE) {
      onClickDelete(item);
    }
  };

  // mange dropdown
  const handleDropdownOpen = useCallback(() => {
    setDropdownOpen(!dropdownOpen);
  }, [dropdownOpen]);

  // handle customer dropdown open/close
  const handleCustomerDropdownToggle = useCallback((customerId: string, isOpen: boolean) => {
    setActiveDropdownId(isOpen ? customerId : null);
  }, []);

  // render more menu items
  const renderMoreMenuItems = useCallback(
    (item: CustomerDetailsInterface) => {
      return CustomerDetailMoreMenu.map((menuItem) => (
        <DropdownItem
          key={menuItem.key}
          label={menuItem.label}
          onClick={() => handleClick(menuItem.key, item)}
          tone={menuItem.label === 'Delete' ? 'critical' : 'primary'}
        />
      ));
    },
    [handleClick]
  );

  //list of customers
  const customerList = useMemo(() => {
    return filteredCustomerList?.map((item, index: number) => {
      if (!buttonRef?.current[index]) {
        buttonRef.current[index] = React.createRef<HTMLDivElement>();
      }

      return (
        <TableRow
          role='button'
          key={item?.id}
          onClick={() => history.push(ROUTES.CUSTOMER_DETAILS.replace(':id', item?.id))}>
          <TableData>
            <Userprofile>
              <Profile>
                <Image
                  imageUrl={item?.profileImage ? item?.profileImage : 'empty_profile'}
                  alt={item?.profileImage ? 'profile' : 'empty_profile'}
                  width={28}
                  height={28}
                />
              </Profile>
              <UserDetails>
                <Username>{item?.companyName}</Username>
                {(!isEmpty(item?.firstName) || !isEmpty(item?.lastName)) && (
                  <UserDescription>
                    {item?.firstName} {item?.lastName}
                  </UserDescription>
                )}
              </UserDetails>
            </Userprofile>
          </TableData>
          <TableData>
            <UserMail>{item?.email}</UserMail>
          </TableData>
          <TableData>
            <UserMail>{item?.phone}</UserMail>
          </TableData>
          <TableData>
            <Dropdown
              activeClassName='active'
              modalTitle='Manage Customer'
              content={
                <OptionMenu className={activeDropdownId === item?.id ? 'active-state' : ''}>
                  <IconButton
                    iconName='three-dots-horizontal-icon'
                    size={isMobile ? ICON_BUTTON_SIZE.MEDIUM : ICON_BUTTON_SIZE.SMALL}
                    variant={ICON_BUTTON_TYPE.TERTIARY}
                    className='more-icon'
                  />
                </OptionMenu>
              }
              trigger='click'
              updateDropdownOpen={(isOpen) => handleCustomerDropdownToggle(item?.id, isOpen)}>
              {renderMoreMenuItems(item)}
            </Dropdown>
          </TableData>
        </TableRow>
      );
    });
  }, [filteredCustomerList, activeDropdownId, isMobile, renderMoreMenuItems, history, handleCustomerDropdownToggle]);

  return (
    <>
      <AppLayout>
        <Group className='desktop-content'>
          <TopBlock>
            {!isMobile ? (
              <HeaderBlock>
                <HeaderLeft>
                  <Heading>
                    <Icon>
                      <SVGIcon
                        name='customer-icon'
                        width={ICON_SIZE.LG}
                        height={ICON_SIZE.LG}
                        viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                        fill='var(--brand-primary)'
                      />
                    </Icon>
                    <Title>Customers</Title>
                  </Heading>
                </HeaderLeft>

                {filteredCustomerList?.length > 0 && (
                  <Button
                    title='New Customer'
                    variant={BUTTON_TYPE.PRIMARY}
                    iconName='plus-icon'
                    size='small'
                    iconPosition='start'
                    onClick={handleOpen}
                    buttonType='button'
                  />
                )}
              </HeaderBlock>
            ) : (
              <ResponsiveHeader title={'Customers'} />
            )}

            {/* empty state component and loading state component */}
            {filteredCustomerList?.length === 0 && !customerLoading && (
              <EmptyStateV2
                size={EMPTY_STATE_SIZE.MEDIUM}
                title={'You have no customers'}
                description={'You can add new customer by clicking the button below.'}
                imageUrl={'customer_empty'}
                buttonTitle={'New Customer'}
                onClick={handleOpen}
                isAlignedCenter={true}
              />
            )}
            {customerLoading && filteredCustomerList?.length === 0 && <LoadingState />}

            {filteredCustomerList?.length > 0 && (
              <TableWrapper>
                <Table>
                  <TableHead>
                    <tr>
                      <TableHeader>
                        <Userprofile>
                          <ProfileIcon>
                            <SVGIcon
                              name='customer-icon'
                              width={ICON_SIZE.XL}
                              height={ICON_SIZE.XL}
                              viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                              fill='var(--text-primary)'
                            />
                          </ProfileIcon>
                          <TitleWrap>Name</TitleWrap>
                        </Userprofile>
                      </TableHeader>
                      <TableHeader>
                        <UserMail>Email</UserMail>
                      </TableHeader>
                      <TableHeader>
                        <UserMail>Phone</UserMail>
                      </TableHeader>
                      <TableHeader>
                        <Icon>
                          <SVGIcon
                            name=''
                            width={ICON_SIZE.XL}
                            height={ICON_SIZE.XL}
                            viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                            fill='none'
                          />
                        </Icon>
                      </TableHeader>
                    </tr>
                  </TableHead>
                  <tbody>{customerList}</tbody>
                </Table>
              </TableWrapper>
            )}
          </TopBlock>
          {filteredCustomerList?.length > 0 && (
            <PaginationSection>
              <LeftDiv>
                <Text>Show</Text>
                <Dropdown
                  modalTitle='Show'
                  content={
                    <ShowRow onClick={() => handleDropdownOpen()}>
                      <RowText>{`${pageData.numOfData} Rows`}</RowText>
                      <Downarrow>
                        <SVGIcon
                          name='down-arrow-icon'
                          width={ICON_SIZE.MD}
                          height={ICON_SIZE.MD}
                          viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                          fill='var(--text-secondary)'
                          className={dropdownOpen ? 'arrow-open' : ''}
                        />
                      </Downarrow>
                    </ShowRow>
                  }
                  trigger='click'
                  onOutsideClick={() => setDropdownOpen(false)}>
                  <>
                    {rowOptions.map((rows) => (
                      <DropdownItem
                        key={rows}
                        label={`${rows} Rows`}
                        onClick={() => handleMenuClick({ key: String(rows) })}
                      />
                    ))}
                  </>
                </Dropdown>
              </LeftDiv>
              <RightDiv>
                <Text>{`${customerListIndex?.start} - ${
                  customerListIndex?.end > customers?.length ? customers?.length : customerListIndex?.end
                } of ${customers?.length}`}</Text>
                <ArrowBtn>
                  <IconButton
                    iconName='new-back-arrow-icon'
                    size={ICON_BUTTON_SIZE.LARGE}
                    variant={isMobile ? ICON_BUTTON_TYPE.TERTIARY : ICON_BUTTON_TYPE.SECONDARY}
                    className='left-arrow-icon'
                    type='rounded'
                    onClick={() => onChangePage('Backward')}
                  />
                  <IconButton
                    iconName='updated-right-arrow-icon'
                    size={ICON_BUTTON_SIZE.LARGE}
                    variant={isMobile ? ICON_BUTTON_TYPE.TERTIARY : ICON_BUTTON_TYPE.SECONDARY}
                    className='right-arrow-icon'
                    type='rounded'
                    onClick={() => onChangePage('Forward')}
                  />
                </ArrowBtn>
              </RightDiv>
            </PaginationSection>
          )}
        </Group>
      </AppLayout>
      <BottomSubNavigation rightElement={<BottomNavigationItem iconName='plus-icon' onClick={handleOpen} />} />
      <Modal open={open} onClose={handleClose} maxWidth={'692px'} isResponsiveMaxWidth={true}>
        <CustomerModal handleCloseModal={handleClose} loadData={loadData} />
      </Modal>
      <Modal open={isDeleteOpen} onClose={onCloseModal} maxWidth={'334px'}>
        <DeleteModal
          onClose={onCloseModal}
          onOk={() => onDeleteCustomer(newCustomer)}
          loading={loading}
          modalTitle='Delete Customer?'
          description='Are you sure to want to delete this customer?'
        />
      </Modal>
    </>
  );
};

export default observer(Customer);
